
import Vue from 'vue'
import * as Api from '@/api/PartyIdentityUserApi'
import { PartyIdentityUserDto } from '@ViewModels/PartyIdentityUserDto'
import { PartySchoolTeacherOptionsModel } from '@ViewModels/PartySchoolTeacherOptionsModel'
import { PlanCopyDto } from '@ViewModels/PlanCopyDto'
import { Alert } from '@/components'
import { SaveButton } from '@/components/buttons'
import { DropDownSelect } from '@/components/formGroups'
import { SelectTable } from '@/components/lists'

export default Vue.extend({
   name: 'CopyPlans',
   props: { schoolId: String, editingItem: Object as () => PartyIdentityUserDto },
   data() {
      return {
         schoolTeacherOptions: [] as NameValuePair[],
         plans: [] as PlanCopyDto[],
         copyQueue: [] as PlanCopyDto[],
         currentSchoolName: '',
         fromSchoolTeacherId: '',
         toSchoolTeacherId: '',
         processing: false,
         processed: false,
         loading: false,
         headers: [
            { name: 'Number', title: 'Number' },
            { name: 'Name', title: 'Plan Title' },
            { name: 'SourcePlanNumber', title: 'From' },
            { name: 'SourceSchoolName', title: '' },
            { name: 'TargetPlanNumber', title: 'To' },
            { name: 'TargetSchoolName', title: '' },
            { name: 'Copying', title: '', type: 'action', data: { text: 'Copying' } },
            {
               name: 'Copied',
               title: '',
               type: 'status',
               data: {
                  true: { text: 'Copied', type: 'success', icon: 'glyphicon-ok' },
               },
            },
         ] as IDataTableColumn[],
      }
   },
   computed: {
      canProcess(): boolean {
         return this.selectedPlans.length > 0 && this.fromSchoolTeacherId !== this.toSchoolTeacherId
      },
      canLoadPlans(): boolean {
         return (
            this.schoolTeacherOptions.length >= 2 &&
            this.fromSchoolTeacherId !== this.toSchoolTeacherId
         )
      },
      selectedPlans(): PlanCopyDto[] {
         return this.plans.filter((p: PlanCopyDto) => p.IsSelected)
      },
      copyToSchoolName(): string {
         const teacher = this.schoolTeacherOptions.find(
            (opt: NameValuePair) => opt.value === this.toSchoolTeacherId
         )
         return teacher ? teacher.name : ''
      },
   },
   mounted() {
      this.loadTeacherOptions()
   },
   methods: {
      async loadTeacherOptions() {
         const teachers = await Api.getSchoolTeacherOptions(this.editingItem.Id)
         this.schoolTeacherOptions = teachers.map((t: PartySchoolTeacherOptionsModel) => ({
            name: t.SchoolName,
            value: t.SchoolTeacherId,
         }))
         let activeTeacher = teachers.find((t: PartySchoolTeacherOptionsModel) => t.IsActive)
         if (!activeTeacher) {
            activeTeacher = teachers[0]
         }
         if (activeTeacher) {
            this.fromSchoolTeacherId = activeTeacher.SchoolTeacherId
            this.toSchoolTeacherId = activeTeacher.SchoolTeacherId
            this.currentSchoolName = activeTeacher.SchoolName
         }
      },
      async loadPlans() {
         this.loading = true
         try {
            const plans = await Api.getPlansForUserPlanMigration(
               this.fromSchoolTeacherId,
               this.toSchoolTeacherId
            )
            this.plans = plans
         } finally {
            this.loading = false
         }
      },
      async action() {
         if (this.selectedPlans.length === 0) {
            return
         }
         try {
            this.processing = true
            this.copyQueue = [...this.selectedPlans]
            this.copyPlanFromQueue()
         } finally {
            this.processing = false
         }
      },
      async copyPlanFromQueue() {
         const currentPlan = this.copyQueue.shift()

         if (!currentPlan) {
            this.$toast.success('All plans copied')
            this.processing = false
            return
         }

         this.copyPlan(currentPlan)
      },
      async copyPlan(plan: PlanCopyDto) {
         plan.Copying = true
         const result = await Api.copyPlanForUserPlanMigration(
            plan.Id,
            this.fromSchoolTeacherId,
            this.toSchoolTeacherId
         )
         plan.TargetSchoolName = this.copyToSchoolName
         plan.TargetPlanNumber = (result.Data as any).TargetPlanNumber
         plan.Copying = false
         if (result.HasError) {
            this.$toast.error(result.ErrorMessage)
         } else {
            plan.Copied = true
         }
         this.copyPlanFromQueue()
      },
   },
   components: {
      Alert,
      SaveButton,
      DropDownSelect,
      SelectTable,
   },
})
